import { makeStyles } from "@material-ui/core";
import { useContext, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import openSocket from "socket.io-client";
import { KTSVG } from '../../../_metronic/helpers';
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import QueueModal from "../../components/Queue/queueModal";
import HistoryAccessModal from "../../components/Queue/historyAccessModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

import FormControl from "@material-ui/core/FormControl";
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import socket from "../../hooks/useSocket";
import { Button } from "@material-ui/core";
import SettingsIcon from '@material-ui/icons/Settings';


const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  customTableCell: {
    display: "flex",
    alignItems: "center",
  },
  modalStyle: {
    minWidth: '310px',
    margin: '20px 10px!important',
    padding: '10px',
		background: '#fff',
		borderRadius: '10px',
    "@media (max-width: 740px)": {
      padding: '0px !important',
      marginTop: '65px'
    }
  },
  icon: {
    color: '#a1a5b7',
    '&:hover': {
      color: '#0b708c'
    }
  }
}));

const reducer = (state, action) => {

  if (action.type === "LOAD_QUEUES") {
    const queues = action.payload;
    const newQueues = [];

    queues.forEach((queue) => {
      const queueIndex = state.findIndex((q) => q.id === queue.id);
      if (queueIndex !== -1) {
        state[queueIndex] = queue;
      } else {
        newQueues.push(queue);
      }
    });

    return [...state, ...newQueues];
  }

  if (action.type === "UPDATE_QUEUES") {
    const queue = action.payload;
    const queueIndex = state.findIndex((u) => u.id === queue.id);

    if (queueIndex !== -1) {
      state[queueIndex] = queue;
      return [...state];
    } else {
      return [queue, ...state];
    }
  }

  if (action.type === "DELETE_QUEUE") {
    const queueId = action.payload;
    const queueIndex = state.findIndex((q) => q.id === queueId);
    if (queueIndex !== -1) {
      state.splice(queueIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const Queues = () => {

  const classes = useStyles();
  const [queues, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [queueModalOpen, setQueueModalOpen] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  
  const { user } = useContext(AuthContext);
  const { adminId, profile } = user;


  const [getUsers, setGetUsers] = useState([]);
  const [selectedUserFilter, setSelectedUserFilter] = useState("");

  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();
    
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/usersList/", {
            signal: abortController.signal,
          });
          if (!isMounted) return;
          setGetUsers(data)
        } catch (err) {
          if (!isMounted || err.name == 'AbortError') return;
          console.error({err});
          toastError(err);
        }
      };

      fetchContacts();
    }, 500);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(delayDebounceFn);
    };
  }, []);


  const handleChange = async (event) => {
    if (event.target.value === "") {
      setSelectedUserFilter("");
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get("/queue");
          dispatch({ type: "RESET" });
          dispatch({ type: "LOAD_QUEUES", payload: data });
          setLoading(false);
        } catch (err) {
          console.error({err});
          toastError(err);
          setLoading(false);
        }
      })();

    } else {
      setSelectedUserFilter(event.target.value);
      (async () => {
        setLoading(true);
        try {
          const { data } = await api.get(`/queueList/${event.target.value}`);
          dispatch({ type: "RESET" });
          dispatch({ type: "LOAD_QUEUES", payload: data });
          setLoading(false);
        } catch (err) {
          console.error({err});
          toastError(err);
          setLoading(false);
        }
      })();
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/queue");
        dispatch({ type: "LOAD_QUEUES", payload: data });
        setLoading(false);
      } catch (err) {
        console.error({err});
        toastError(err);
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const queueEventName = `queue-${user.adminId}`;
    const queueEventFunction = async (data) => {
      if ((data.action === "update" || data.action === "create") && adminId == data.queue.adminId) {
        dispatch({ type: "UPDATE_QUEUES", payload: data.queue });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUEUE", payload: data.queueId });
      }
    };
    socket.on(queueEventName, queueEventFunction);

    return () => {
      socket.off(queueEventName, queueEventFunction);
    };
  }, []);

  const handleOpenQueueModal = () => {
    setQueueModalOpen(true);
    setSelectedQueue(null);
  };

  const handleCloseQueueModal = () => {
    setQueueModalOpen(false);
    setSelectedQueue(null);
  };

  const handleEditQueue = (queue) => {
    setSelectedQueue(queue);
    setQueueModalOpen(true);
  };

  const handleCloseConfirmationModal = () => {
    setConfirmModalOpen(false);
    setSelectedQueue(null);
  };

  const handleDeleteQueue = async (queueId) => {
    try {
      await api.delete(`/queue/${queueId}`);
      toast.success(i18n.t("Fila deletada com sucesso!"));
    } catch (err) {
      console.error({err});
      toastError(err);
    }
    setSelectedQueue(null);
  };

  const handleOpenHistoryModal = (queueId) => {
    setSelectedHistory(queueId);
    setSelectedQueue(null);
  };

  const handleCloseHistoryModal = () => {
    setSelectedHistory(null);
    setSelectedQueue(null);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          selectedQueue &&
          `${i18n.t("queues.confirmationModal.deleteTitle")} ${selectedQueue.name}?`
        }
        open={confirmModalOpen}
        onClose={handleCloseConfirmationModal}
        onConfirm={() => handleDeleteQueue(selectedQueue.id)}
      >
        {selectedQueue && `${i18n.t("queues.confirmationModal.deleteMessage")} ${selectedQueue.name}?`}
      </ConfirmationModal>
      <QueueModal
        open={queueModalOpen}
        onClose={handleCloseQueueModal}
        queueId={selectedQueue?.id}
      />
      <HistoryAccessModal
        open={selectedHistory != null}
        onClose={handleCloseHistoryModal}
        queueId={selectedHistory}
      />
      <div class='row gy-5 gx-xl-8'
        className={classes.modalStyle}
      >
        <div className='col-xl-15'>
          <div className='card card-xxl-stretch-5 mb-15 mb-xl-8'>
            {/* begin::Header */}
            <div className='card-header border-0 pt-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Filas</span>
                {/*<span className='text-muted mt-1 fw-bold fs-7'>Over 500 members</span>*/}
              </h3>
              <div
                className='card-toolbar '
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                data-bs-trigger='hover'
              >
                <FormControl variant="outlined" hidden={user.profile != 'admin'}>
                  <Select
                    value={selectedUserFilter}
                    displayEmpty
                    style={{
                      padding: '1px 0px 4px',
                      margin: '4px 10px -2px 0px',
                      height: '30px',
                      width: '173px',
                      fontSize: '15px'
                    }}
                    inputProps={{
                      style: {
                        padding: 5
                      }
                    }}
                    onChange={e => handleChange(e)}
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}

                  >
                    <MenuItem value="">
                      <em>Todos os usuários</em>
                    </MenuItem>
                    {getUsers.map((user, key) => (
                      <MenuItem dense key={key} value={user.id}>
                        <ListItemText primary={user.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  style={{
                    marginLeft: "1.5px",
                  }}
                  variant="contained"
                  color="primary"
                  onClick={handleOpenQueueModal}
                >
                  <KTSVG path='media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  Adicionar Fila
                </Button>
              </div>
            </div>
            {/* end::Header */}
            {/* begin::Body */}
            <div className='card-body py-3'>
              {/* begin::Table container */}
              <div className='table-responsive'>
                {/* begin::Table */}
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                  {/* begin::Table head */}
                  <thead>
                    <tr className='fw-bolder text-muted' >
                      <th className='min-w-150px'>ID</th>
                      <th className='min-w-150px'>Nome</th>
                      <th className='min-w-140px'>Cor</th>
                      <th className='min-w-120px'>Mensagem de saudação</th>
                      <th className='min-w-100px text-end'>Ações</th>
                    </tr>
                  </thead>
                  {/* end::Table head */}
                  {/* begin::Table body */}
                  <tbody>
                    <>
                      {queues.map((queue) => (
                        <>
                          <tr>
                            <td style={{ width: 10,alignSelf: "center",}}>
                                <div className={classes.customTableCell}>
                                    {queue.id}
                                </div>
                            </td>
                            <td>
                              <div>
                                <div className='d-flex justify-content-start flex-column'>
                                    {queue.name}
                                </div>
                              </div>
                            </td>
                            <td>
                              <div className={classes.customTableCell}>
                                <span
                                  style={{
                                    backgroundColor: queue.color,
                                    width: 20,
                                    height: 20,
                                    borderRadius: 20,
                                    alignSelf: "center",
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <a className='text-dark fw-bolder text-hover-primary d-block fs-6'>
                                {queue.greetingMessage}
                              </a>
                            </td>
                            <td>
                              <div className='d-flex justify-content-end flex-shrink-0'>

                                <button

                                  title='Editar'
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1'
                                  onClick={() => handleEditQueue(queue)}
                                >
                                  <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                                </button>

                                <button
                                  title='Permissão de acesso ao histórico'
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                  onClick={() => handleOpenHistoryModal(queue.id)}
                                >
                                  <SettingsIcon className={classes.icon} />
                                </button>

                                <button
                                  title='Excluir'
                                  className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm'
                                  onClick={() => {
                                    setSelectedQueue(queue);
                                    setConfirmModalOpen(true);
                                  }}
                                >
                                  <KTSVG
                                    path='/media/icons/duotune/general/gen027.svg'
                                    className='svg-icon-3'
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        </>
                      ))}
                      {loading && <TableRowSkeleton columns={4} />}
                    </>
                  </tbody>
                  {/* end::Table body */}
                </table>
                {/* end::Table */}
              </div>
              {/* end::Table container */}
            </div>
            {/* begin::Body */}
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default Queues;
